import {
  ControlOutlined,
  DashboardOutlined,
  EditOutlined,
  ExperimentOutlined,
  SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import OrganisationAvatar from 'Components/Avatar/OrganisationAvatar'
import MenuItem from 'Components/Menu/MenuItem'
import SubMenu from 'Components/Menu/SubMenu'
import { useCurrentOrganisation } from 'Hooks/useCurrentOrganisation'
import { HomeRow, MenuTitle } from 'Layouts/styled'
import { OrganisationsService } from 'Service'
import navMenuStyles from 'Styles/NavMenu.module.scss'
import { Space, Tag } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  AuthorizationWrapperV2,
  checkAuthorizationV2,
} from '../../Models/AuthorizeV2/checkAuthorizationV2'
import PolicyMappings from '../../Utils/policyMapping'
import AuthBaseLayout from '../AuthBaseLayout'

const buildMenus = ['/configuration', '/integration', '/prompts']
const monitorMenus = ['/calls', '/analytics', '/data-export']
const controlMenus = [
  '/batch-manager',
  'campaigns',
  '/batch-rules',
  '/dnd-manager',
  '/payment-files',
]

export const VoiceAgentAppConfigLayout = () => {
  const dispatch = useDispatch()
  const flowLayout: any = useSelector<any>((s) => s.flowLayout)

  const params = useParams()
  const location = useLocation()
  const [queryParams] = useSearchParams()
  const navigate = useNavigate()
  const [activeSubMenu, setActiveSubMenu] = useState<string>('')
  const { organisation } = useCurrentOrganisation()
  const [experimentQueryParam, setExperimentQueryParam] = useState<string>('')
  const flowUuid = params?.flow_uuid || ''
  const orgUuid = params?.organisation_uuid || ''
  const experimentFlow = queryParams.get('experiment')

  useEffect(() => {
    dispatch({
      type: 'organisations/retrieve',
      uuid: params?.organisation_uuid,
    })
  }, [params?.organisation_uuid, dispatch])

  const fetchFlow: any = async (flowUuid: string, orgUuid: string) => {
    try {
      const response = await OrganisationsService.organisationsFlowsRetrieve({
        uuid: flowUuid,
        organisationUuid: orgUuid,
      })
      return response
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (experimentFlow !== null && experimentFlow.toLowerCase() === 'true' && flowUuid) {
      setExperimentQueryParam('?experiment=true')
    } else {
      setExperimentQueryParam('')
    }
  }, [experimentFlow, experimentQueryParam, flowUuid])

  useEffect(() => {
    const authorizedForExperiment = checkAuthorizationV2(
      {
        requiredPolicies: PolicyMappings.appSettingsExperimentSettings,
        locationReference: {
          flowUUID: flowUuid,
          organisationUUID: params?.organisation_uuid,
        },
      },
      []
    )
    if (
      authorizedForExperiment &&
      experimentFlow !== null &&
      experimentFlow.toLowerCase() === 'true' &&
      flowUuid
    ) {
      fetchFlow(flowUuid, orgUuid)
        .then((response: any) => {
          if (
            typeof response?.experiment_version === 'string' &&
            response?.experiment_version !== null
          ) {
            dispatch({
              type: 'flowLayout/setFlow',
              uuid: params.flow_uuid,
              organisationUuid: params.organisation_uuid,
              version: '0.0.2',
            })
          } else {
            dispatch({
              type: 'flowLayout/setFlow',
              uuid: params.flow_uuid,
              organisationUuid: params.organisation_uuid,
              version: '0.0.1',
            })
          }
        })
        .catch((e: any) => {
          console.error(e)
          dispatch({
            type: 'flowLayout/setFlow',
            uuid: params.flow_uuid,
            organisationUuid: params.organisation_uuid,
            version: '0.0.1',
          })
        })
    } else {
      dispatch({
        type: 'flowLayout/setFlow',
        uuid: params.flow_uuid,
        organisationUuid: params.organisation_uuid,
        version: '0.0.1',
      })
    }
  }, [dispatch, experimentFlow, flowUuid, orgUuid, params.flow_uuid, params.organisation_uuid])

  const selected: any = useMemo(() => {
    if (buildMenus.some((x) => location.pathname.includes(x))) {
      return { build: true }
    } else if (monitorMenus.some((x) => location.pathname.includes(x))) {
      return { monitor: true }
    } else if (controlMenus.some((x) => location.pathname.includes(x))) {
      return { controlPanel: true }
    }
    return false
  }, [location.pathname])

  const isTestPage = useCallback(() => {
    if (location.pathname.includes('golden-calls') || location.pathname.includes('pipeline')) {
      return true
    }
    return false
  }, [location.pathname])

  return (
    <AuthBaseLayout
      collapsibleSideNav={true}
      sideNavTheme="dark"
      className={[navMenuStyles['menu-item'], navMenuStyles['menu-item__dark']].join(' ')}
      sideMenuProps={{
        style: {
          backgroundColor: 'var(--blue-600)',
        },
        theme: 'dark',
        defaultOpenKeys: [],
      }}
      menu={[
        <AuthorizationWrapperV2
          key="build-auth"
          requiredPolicies={[
            ...PolicyMappings.appConfigureViewTab,
            ...PolicyMappings.appIntegrationsViewTab,
            ...PolicyMappings.appPromptOptionsViewTab,
          ]}
        >
          <SubMenu
            key="config-sub-menu"
            title={'Build'}
            submenuKey={'build'}
            activeKey={activeSubMenu}
            icon={<EditOutlined />}
            defaultOpen={selected.build}
            selected={selected.build}
            theme="dark"
            onOpen={() => setActiveSubMenu('build')}
          >
            <AuthorizationWrapperV2 requiredPolicies={PolicyMappings.appConfigureViewTab}>
              <MenuItem
                key="configure"
                subMenuItem
                to={`configuration${experimentQueryParam}`}
                selected={location.pathname.includes('configuration')}
                theme="dark"
              >
                Configure
              </MenuItem>
            </AuthorizationWrapperV2>
            <AuthorizationWrapperV2 requiredPolicies={PolicyMappings.appIntegrationsViewTab}>
              <MenuItem
                key="integrations"
                subMenuItem
                to={`integration${experimentQueryParam}`}
                selected={location.pathname.includes('integrations')}
                theme="dark"
              >
                Integrations
              </MenuItem>
            </AuthorizationWrapperV2>
            <AuthorizationWrapperV2 requiredPolicies={PolicyMappings.appPromptOptionsViewTab}>
              <MenuItem
                key="Prompts"
                subMenuItem
                to={`prompts${experimentQueryParam}`}
                selected={location.pathname.includes('Prompts')}
                theme="dark"
              >
                Prompts
              </MenuItem>
            </AuthorizationWrapperV2>
          </SubMenu>
        </AuthorizationWrapperV2>,

        // <MenuItem key="account" to={`account`} icon={<RocketOutlined />} theme="dark">
        //   <ComingSoonMenu title="Account" theme="dark"></ComingSoonMenu>
        // </MenuItem>,
        <AuthorizationWrapperV2
          key={'e2e-test-auth'}
          requiredPolicies={[
            ...PolicyMappings.appTestViewPipeline,
            ...PolicyMappings.appTestViewGoldenCall,
          ]}
        >
          <MenuItem
            key="test"
            to={`test`}
            icon={<ExperimentOutlined />}
            theme="dark"
            selected={isTestPage()}
          >
            <Space align={'center'} wrap>
              Test{' '}
              <Tag color="#E68C04" style={{ borderRadius: '20px' }}>
                Beta
              </Tag>
            </Space>
          </MenuItem>
        </AuthorizationWrapperV2>,

        <AuthorizationWrapperV2
          debug
          key="analytics"
          requiredPolicies={[
            ...PolicyMappings.appAnalyticsViewTab,
            ...PolicyMappings.appCallsViewCalls,
          ]}
        >
          <SubMenu
            key="monitor-sub-menu"
            title="Monitor"
            activeKey={activeSubMenu}
            submenuKey={'monitor'}
            icon={<DashboardOutlined />}
            theme="dark"
            selected={selected.monitor}
            defaultOpen={selected.monitor}
            onOpen={() => setActiveSubMenu('monitor')}
          >
            <AuthorizationWrapperV2 requiredPolicies={[...PolicyMappings.appAnalyticsViewTab]}>
              <MenuItem
                key="analytics"
                to={`analytics`}
                theme="dark"
                selected={location.pathname.includes('analytics')}
                subMenuItem
              >
                Analytics
              </MenuItem>
            </AuthorizationWrapperV2>
            <AuthorizationWrapperV2 debug requiredPolicies={[...PolicyMappings.appCallsViewCalls]}>
              <MenuItem
                key="calls"
                to={`calls`}
                theme="dark"
                selected={location.pathname.includes('calls')}
                subMenuItem
              >
                Calls
              </MenuItem>
            </AuthorizationWrapperV2>
            <AuthorizationWrapperV2 debug requiredPolicies={PolicyMappings.appMonitorDataExport}>
              <MenuItem
                key="data-export"
                to={`data-export`}
                theme="dark"
                selected={location.pathname.includes('data-export')}
                subMenuItem
              >
                Data Export
              </MenuItem>
            </AuthorizationWrapperV2>
          </SubMenu>
        </AuthorizationWrapperV2>,
        <AuthorizationWrapperV2
          key={'control-panel-auth'}
          requiredPolicies={[
            ...PolicyMappings.appSettingsAgentSearchOnly,
            ...PolicyMappings.appSettingsAgentView,
          ]}
        >
          <MenuItem key="agent" to={`agent`} icon={<SwapOutlined />} theme="dark">
            Agent
          </MenuItem>
        </AuthorizationWrapperV2>,
      ]}
      bottomMenu={[
        <AuthorizationWrapperV2
          key="settings-auth"
          requiredPolicies={[
            ...PolicyMappings.appSettingsModifyVoiceAgent,
            ...PolicyMappings.appSettingsUpgradeToBetaButton,
            ...PolicyMappings.appSettingsUpgradeToBetaButton,
          ]}
        >
          <MenuItem
            key="settings"
            to={`settings`}
            icon={<SettingOutlined />}
            theme="dark"
            style={{ marginTop: 'auto' }}
          >
            Settings
          </MenuItem>
        </AuthorizationWrapperV2>,
      ]}
      headerRender={({ theme, collapsed }: any) => {
        return (
          <HomeRow theme={theme}>
            <OrganisationAvatar name={organisation?.name} />
            {!collapsed ? (
              <MenuTitle theme={theme}>{flowLayout?.flow?.name || ''}</MenuTitle>
            ) : null}
          </HomeRow>
        )
      }}
    >
      <Outlet />
    </AuthBaseLayout>
  )
}
